import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {addStyle, View, type StyleType} from "@khanacademy/wonder-blocks-core";
import {semanticColor, sizing} from "@khanacademy/wonder-blocks-tokens";
import {LabelXSmall} from "@khanacademy/wonder-blocks-typography";
import {a11yStyles} from "@khan/shared-styles";

import {getCellBorderStyles} from "./border-styles.ts";
// UI components
import Pagination from "./pagination.tsx";
import {SortableHeader} from "./sortable-header.tsx";
import type {Column} from "./types.ts";
import usePagination from "./use-pagination.ts";
// hooks
import useSortable from "./use-sortable.ts";
// helpers
import {getValueFromKey} from "./utils.ts";

import ArrowLeftIcon from "@phosphor-icons/core/regular/arrow-left.svg";
import ArrowRightIcon from "@phosphor-icons/core/regular/arrow-right.svg";

const StyledCaption = addStyle("caption");
const StyledTable = addStyle("table");
const StyledTr = addStyle("tr");
const StyledTd = addStyle("td");
const StyledTh = addStyle("th");

interface Props<T> {
    /**
     * The data to render in the table.
     * NOTE: We use generic types here to ensure that the data passed in is of
     * the same type as the data in the rows.
     */
    data: Array<T>;
    /**
     * The columns to include in the table.
     */
    columns: Array<Column<T>>;
    /**
     * The caption associated with the table. This is used to describe the table
     * to screen readers.
     */
    caption?: string;
    /**
     * The summary row for the table. This row will not be sortable or paginated.
     */
    summaryRow?: T;
    /**
     * An identifier for the table. This is used to associate the table with the
     * caption.
     */
    id?: string;
    /**
     * The default sort key for the table. If set, it will get the order from
     * the column info.
     */
    defaultSortKey?: string;
    /**
     * The number of items to show per page.
     */
    pageSize?: number;
    /**
     * Whether to render the pagination after the table.
     */
    paginationAfterTable?: boolean;
    /**
     * Column pagination: Whether to enable column pagination and show the previous and next buttons.
     */
    enableColumnPagination?: boolean;
    /**
     * Column pagination: The custom labels for the previous and next buttons.
     */
    columnPaginationButtonLabels?: {
        previous: string;
        next: string;
    };
    /**
     * Column pagination: A function to call when the column page changes.
     */
    onColumnPageClick?: (direction: "next" | "previous") => void;
    /**
     * Column pagination: Whether there is a next page.
     * This is used to enable/disable the next button for column pagination.
     */
    hasNextPage?: boolean;
    /**
     * Column pagination: Whether there is a previous page.
     * This is used to enable/disable the previous button for column pagination.
     */
    hasPreviousPage?: boolean;
    /**
     * Custom styles for the rows.
     */
    rowStyle?: StyleType;
    /**
     * Custom styles for the table.
     */
    headerStyle?: StyleType;
    /**
     * Custom cell styles for the table.
     */
    cellStyles?: StyleType;
    /**
     * Optional inlineBorder
     */
    inlineBorder?: boolean;
    /**
     * Optional custom sort icons for table headers.
     */
    sortIcons?: {
        ascending: PhosphorBold | PhosphorFill | PhosphorRegular;
        descending: PhosphorBold | PhosphorFill | PhosphorRegular;
    };
    /**
     * A function to call when the page changes. This is useful for
     * server-side pagination. To check the page number synchronously,
     * just return `Promise.resolve()` from the callback.
     * @param page The new page number.
     * @returns A promise that resolves before the page changes.
     * @example
     * ```tsx
     * const {data, cursor} = useData();
     * const pageSize = 10;
     * const fullPagesLoaded = Math.ceil(data.length / pageSize);
     *
     * <SimpleTable
     *    data={data}
     *    columns={columns}
     *    pageSize={pageSize}
     *    onPageChange={async (nextPage) => {
     *        // If the next page is last page and the cursor is not null,
     *        // fetch the next chunk of data.
     *        if (nextPage >= fullPagesLoaded && cursor) {
     *            return await fetchData({cursor});
     *        }
     *   }}
     * />
     * ```
     */
    onPageChange?: (nextPage: number) => Promise<void>;
    /**
     * The current page to display in the table.
     * This allows the parent component to control the pagination state.
     */
    currentPage?: number;
    /**
     * For row pagination.
     * Whether there is more data to load.
     * This is used to determine if more data can be fetched so pagination can
     * can announce that total pages is uncertain.
     */
    hasMorePages?: boolean;
}

/**
 * Renders a simple table that supports sorting and pagination.
 */
export default function SimpleTable<T>({
    data,
    caption,
    columns,
    defaultSortKey,
    id = "table-id",
    pageSize,
    summaryRow,
    rowStyle,
    headerStyle,
    inlineBorder,
    sortIcons,
    paginationAfterTable,
    cellStyles,
    enableColumnPagination = false,
    columnPaginationButtonLabels,
    onColumnPageClick,
    hasNextPage,
    hasPreviousPage,
    onPageChange,
    currentPage,
    hasMorePages,
}: Props<T>): React.ReactElement {
    // --- Column pagination setup ---
    function renderColumnPaginationControls() {
        if (!enableColumnPagination) {
            return null;
        }
        return (
            <StyledTr style={styles.columnPaginationWrapper}>
                <StyledTh colSpan={columns.length} scope="col">
                    <View
                        style={[
                            styles.columnPaginationButtonsContainer,
                            inlineBorder &&
                                styles.columnPaginationButtonsContainerBorder,
                        ]}
                    >
                        <Button
                            kind="tertiary"
                            onClick={
                                onColumnPageClick &&
                                (() => onColumnPageClick("previous"))
                            }
                            disabled={!hasPreviousPage}
                            startIcon={ArrowLeftIcon}
                            size="small"
                        >
                            {columnPaginationButtonLabels?.previous ||
                                t`Previous columns`}
                        </Button>
                        <Button
                            kind="tertiary"
                            onClick={
                                onColumnPageClick &&
                                (() => onColumnPageClick("next"))
                            }
                            disabled={!hasNextPage}
                            endIcon={ArrowRightIcon}
                            size="small"
                        >
                            {columnPaginationButtonLabels?.next ||
                                t`Next columns`}
                        </Button>
                    </View>
                </StyledTh>
            </StyledTr>
        );
    }

    // ---Sorting---
    const {onSort, sort, sortedData} = useSortable(
        data,
        columns,
        defaultSortKey,
    );
    const isSortable = columns.some((column) => column.sortable);

    // Ensure that the page size is either a finite, positive number or
    // POSITIVE_INFINITY (i.e. no paginiation). So null, undefined, NaN, 0, any
    // negative number, or negative infinity will result in
    // no pagination -- indicated by positive infinity.
    const validatedPageSize: number =
        pageSize && pageSize > 0 ? pageSize : Number.POSITIVE_INFINITY;

    // --- Row pagination---
    const {setPage, page, pageData} = usePagination(
        sortedData,
        currentPage || 1,
        validatedPageSize,
    );

    const totalItems = data.length;

    const [loading, setLoading] = React.useState(false);
    const handlePageChange = React.useCallback(
        async (newPage: number) => {
            if (onPageChange) {
                setLoading(true);
                await onPageChange(newPage);
                setLoading(false);
            }
            setPage(newPage);
        },
        [onPageChange, setPage],
    );

    const paginationArgs = React.useMemo(
        () =>
            ({
                id: `${id}-pagination`,
                page,
                pageSize: validatedPageSize,
                onPageChange: handlePageChange,
                total: totalItems,
                loading,
                hasMorePages: hasMorePages,
            }) satisfies React.ComponentProps<typeof Pagination>,
        [
            id,
            page,
            validatedPageSize,
            handlePageChange,
            totalItems,
            hasMorePages,
            loading,
        ],
    );

    return (
        /**
         * Allows annoucing the table to screen readers.
         * @see https://adrianroselli.com/2017/11/a-responsive-accessible-table.html#ResponsiveScrollingKeyboard
         */
        <View
            role="region"
            aria-labelledby={`${id}-caption`}
            style={paginationAfterTable && styles.paginationBottomWrapper}
        >
            <View tabIndex={0} style={styles.wrapper}>
                <StyledTable id={id} style={styles.table}>
                    {/**
                     * Allows screen readers to read the table caption, so that
                     * they know what the table is about.
                     * @see https://www.w3.org/TR/WCAG20-TECHS/H39
                     */}
                    {caption && (
                        <StyledCaption
                            style={a11yStyles.srOnly}
                            id={`${id}-caption`}
                        >
                            {caption}
                            {isSortable && (
                                <>
                                    {" "}
                                    {t`Column headers with buttons are sortable.`}
                                </>
                            )}
                        </StyledCaption>
                    )}
                    <thead>
                        {/**
                         * These are the buttons for column pagination.
                         * If a number isn't passed in, column pagination will not be enabled.
                         */}
                        {renderColumnPaginationControls()}

                        <StyledTr style={styles.sticky}>
                            {columns.map((column, i) => {
                                const isBeingSorted =
                                    sort?.orderBy === column.dataKey;
                                const sortOrder = isBeingSorted
                                    ? sort?.order
                                    : "none";

                                // To comply with WCAG 1.3.1, we should use the `scope`
                                // attribute to explicitly define the relationship between
                                // header cells and data cells
                                // @see https://www.w3.org/TR/WCAG20-TECHS/H63.html
                                return (
                                    <StyledTh
                                        key={i}
                                        aria-sort={sortOrder}
                                        scope="col"
                                        style={[
                                            styles.tableHeader,
                                            column.style,
                                            headerStyle,
                                        ]}
                                    >
                                        <LabelXSmall>
                                            {column.sortable && sort ? (
                                                <SortableHeader
                                                    column={column}
                                                    onSort={onSort}
                                                    sort={sort}
                                                    {...(sortIcons && {
                                                        sortIcons,
                                                    })}
                                                />
                                            ) : (
                                                column.label
                                            )}
                                        </LabelXSmall>
                                    </StyledTh>
                                );
                            })}
                            {pageSize && !paginationAfterTable && (
                                <StyledTh
                                    style={styles.tableHeader}
                                    scope="col"
                                >
                                    <Pagination {...paginationArgs} />
                                </StyledTh>
                            )}
                        </StyledTr>
                    </thead>
                    <tbody>
                        {summaryRow && (
                            <StyledTr key="summary" style={[rowStyle]}>
                                {columns.map((column, i) => (
                                    <StyledTd
                                        key={`summary-${i}`}
                                        style={[
                                            styles.cell,
                                            cellStyles,
                                            ...getCellBorderStyles(
                                                inlineBorder,
                                                i,
                                                columns.length,
                                                0,
                                                pageData.length + 1, // Account for all rows plus the summary row
                                            ),
                                        ]}
                                    >
                                        {i === 0 && (
                                            <View
                                                id={`${id}-summary`}
                                                style={a11yStyles.srOnly}
                                            >
                                                {t`Summary row`}
                                            </View>
                                        )}
                                        {column.cell
                                            ? column.cell(summaryRow)
                                            : getValueFromKey(
                                                  summaryRow,
                                                  column.dataKey ?? "",
                                              )}
                                    </StyledTd>
                                ))}
                            </StyledTr>
                        )}

                        {pageData.map((row, idx) => (
                            <StyledTr key={idx} style={[rowStyle]}>
                                {columns.map((column, i) => (
                                    <StyledTd
                                        key={i}
                                        style={[
                                            styles.cell,
                                            cellStyles,
                                            ...getCellBorderStyles(
                                                inlineBorder,
                                                i,
                                                columns.length,
                                                idx,
                                                pageData.length,
                                            ),
                                        ]}
                                    >
                                        {column.cell
                                            ? column.cell(row)
                                            : getValueFromKey(
                                                  row,
                                                  column.dataKey ?? "",
                                              )}
                                    </StyledTd>
                                ))}
                            </StyledTr>
                        ))}
                    </tbody>
                </StyledTable>
            </View>
            {pageSize && paginationAfterTable && (
                <View style={styles.paginationBottom}>
                    <Pagination {...paginationArgs} />
                </View>
            )}
        </View>
    );
}

const styles = StyleSheet.create({
    paginationBottomWrapper: {
        paddingBlockEnd: sizing.size_560,
    },
    wrapper: {
        width: "100%",
        overflowX: "auto",
    },
    table: {
        borderCollapse: "separate",
        borderSpacing: 0,
        tableLayout: "fixed",
    },
    tableHeader: {
        padding: `${sizing.size_160} ${sizing.size_160} `,
        textTransform: "uppercase",
        textAlign: "start",
        borderBlockEnd: `${sizing.size_010} solid ${semanticColor.border.primary}`,
        background: semanticColor.surface.secondary,
    },
    sticky: {
        backgroundColor: semanticColor.border.inverse,
        position: "sticky",
        top: 0,
        // Prevents the sticky header from overlapping the table data.
        zIndex: 1,
    },
    cell: {
        padding: sizing.size_200,
        verticalAlign: "middle",
        borderBlockEnd: `${sizing.size_010} solid ${semanticColor.border.primary}`,
    },
    paginationBottom: {
        position: "absolute",
        insetBlockEnd: 0,
        margin: "0 auto",
        insetInline: 0,
    },
    columnPaginationButtonsContainer: {
        flexDirection: "row",
        justifyContent: "space-between",
        paddingInline: sizing.size_040,
        paddingBlock: sizing.size_080,
    },
    columnPaginationButtonsContainerBorder: {
        borderBlockStart: `${sizing.size_010} solid ${semanticColor.border.primary}`,
        borderInline: `${sizing.size_010} solid ${semanticColor.border.primary}`,
        borderStartEndRadius: sizing.size_080,
        borderStartStartRadius: sizing.size_080,
    },
    columnPaginationWrapper: {
        backgroundColor: semanticColor.surface.secondary, // match header bg
        paddingBlock: sizing.size_160, // match table header padding
        paddingInline: sizing.size_160,
        textAlign: "start",
    },
});
